import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://cofoundwith.us";
const DEFAULT_DESCRIPTION =
  "Ship your startup solo. 80+ sequenced AI prompts take you from idea to launch — no team, no budget, no guesswork.";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

interface SeoProps {
  title: string;
  description?: string;
  ogImage?: string;
  path?: string;
  type?: "website" | "article";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const setMeta = (
  key: string,
  content: string,
  attr: "name" | "property" = "property"
) => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href: string) => {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const JSONLD_ID = "seo-route-jsonld";

const setJsonLd = (data?: SeoProps["jsonLd"]) => {
  const existing = document.getElementById(JSONLD_ID);
  if (!data) {
    existing?.remove();
    return;
  }
  const el = (existing as HTMLScriptElement | null) ?? document.createElement("script");
  el.id = JSONLD_ID;
  el.setAttribute("type", "application/ld+json");
  el.textContent = JSON.stringify(data);
  if (!existing) document.head.appendChild(el);
};

const Seo = ({ title, description, ogImage, path, type = "website", jsonLd }: SeoProps) => {
  const location = useLocation();
  const pathname = path ?? location.pathname;
  const desc = description || DEFAULT_DESCRIPTION;
  const image = ogImage || DEFAULT_OG_IMAGE;
  const url = `${SITE_URL}${pathname}`;

  useEffect(() => {
    document.title = title;
    setMeta("description", desc, "name");
    setMeta("og:title", title);
    setMeta("og:description", desc);
    setMeta("og:image", image);
    setMeta("og:url", url);
    setMeta("og:type", type);
    setMeta("twitter:card", "summary_large_image", "name");
    setMeta("twitter:title", title, "name");
    setMeta("twitter:description", desc, "name");
    setMeta("twitter:image", image, "name");
    setCanonical(url);
    setJsonLd(jsonLd);
    return () => setJsonLd(undefined);
  }, [title, desc, image, url, type, jsonLd]);

  return null;
};

export default Seo;
